/**
 * Offering-based entitlement registry - the single FE source of truth binding
 * each offering to its member modules. Mirrors the BE registry
 * (api.serializers.OFFERING_REGISTRY). Both the sidebar (`components/erp/
 * sidebar.tsx`) feature filter and the `FeatureGuard` route guard consume
 * `hasAnyFeature` from here so the offering→module mapping lives in ONE place.
 *
 * Offering keys are the flags stored in `Organization.enabled_features`.
 * Always-on / all-tenant modules are intentionally NOT offerings - they render
 * for every firm (role permitting) and appear read-only in the admin panel's
 * "Always included" footer, driven by `ALWAYS_INCLUDED` below.
 */

export type OfferingKey =
  | "eln"
  | "qc"
  | "production"
  | "ai"
  | "assistant"
  | "insilico"
  | "predictive_retrosynthesis"
  | "tally"

export type OfferingClass = "standard" | "beta"

export interface Offering {
  /** The flag stored in enabled_features. */
  key: OfferingKey
  label: string
  /** Admin-panel copy. */
  description: string
  classification: OfferingClass
  /** Member modules for admin-panel display + traceability; routes are the anchor. */
  modules: { name: string; route: string }[]
}

export const OFFERINGS: Offering[] = [
  {
    key: "eln",
    label: "ELN",
    description: "Electronic lab notebooks - experiments, formulations, and shared instruments/methods.",
    classification: "standard",
    modules: [
      { name: "Experiments", route: "/rnd/eln" },
      { name: "Formulations", route: "/rnd/eln/formulations" },
      { name: "Instruments", route: "/instruments" },
      { name: "Methods", route: "/methods" },
    ],
  },
  {
    key: "qc",
    label: "QC",
    description: "Quality control - samples, QA/QC disposition, and shared instruments/methods.",
    classification: "standard",
    modules: [
      { name: "QA / QC", route: "/qaqc" },
      { name: "Samples", route: "/samples" },
      { name: "Instruments", route: "/instruments" },
      { name: "Methods", route: "/methods" },
    ],
  },
  {
    key: "production",
    label: "Production",
    description: "Plant execution - batches, master batch records, and equipment.",
    classification: "standard",
    modules: [
      { name: "Batches", route: "/batches" },
      { name: "Master Batch Records", route: "/mbr-library" },
      { name: "Equipment", route: "/equipment" },
    ],
  },
  // `ai` vs `assistant` (assistant-offering-split, 2026-09-17): `ai` is the
  // INLINE AI embedded in other modules - notebook scanning, SDS/CoA extraction,
  // TPP drafting, contextual summaries - gated per surface by
  // `useHasFeature("ai")`. It owns no route of its own. `assistant` is the
  // conversational module. A firm can hold `ai` without `assistant` (scanning,
  // no chat); the reverse is forbidden - see OFFERING_DEPENDENCIES.
  {
    key: "ai",
    label: "AI",
    description:
      "Inline AI across the app - notebook scanning, SDS/CoA extraction, TPP drafting, and summaries.",
    classification: "standard",
    modules: [],
  },
  {
    key: "assistant",
    label: "AI Assistant",
    description: "The conversational dxtr assistant. Requires AI.",
    classification: "standard",
    modules: [{ name: "AI Assistant", route: "/assistant" }],
  },
  {
    key: "insilico",
    label: "InSilico",
    description: "In silico formulation and simulation (Beta).",
    classification: "beta",
    modules: [{ name: "InSilico", route: "/rnd/insi" }],
  },
  {
    key: "predictive_retrosynthesis",
    label: "Predictive Retrosynthesis",
    description: "AI-powered retrosynthesis planning (Beta).",
    classification: "beta",
    modules: [{ name: "Predictive Retrosynthesis", route: "/rnd/pr1" }],
  },
  // Integrations - outbound connectors to the customer's accounting/ERP. One
  // key per connector so a firm can hold one without the rest. `tally` unlocks
  // the Settings → Integrations tab for tenant admins (`app/settings/page.tsx`
  // gates it with `useHasFeature("tally")`). It owns no route of its own:
  // Settings is always-on, the TAB inside it is what the offering unlocks.
  {
    key: "tally",
    label: "Tally",
    description: "Tally accounting connector - connect with accounting software.",
    classification: "standard",
    modules: [],
  },
]

/** Admin-panel section order - ELN / QC / Production / AI / Beta / Integrations. */
export const OFFERING_GROUPS: { label: string; keys: OfferingKey[] }[] = [
  { label: "ELN", keys: ["eln"] },
  { label: "QC", keys: ["qc"] },
  { label: "Production", keys: ["production"] },
  { label: "AI", keys: ["ai", "assistant"] },
  { label: "Beta", keys: ["insilico", "predictive_retrosynthesis"] },
  { label: "Integrations", keys: ["tally"] },
]

/**
 * Packaging dependencies between offerings: `{ dependent: required }`. The
 * backend enforces the same map at write time
 * (`api.serializers.OFFERING_DEPENDENCIES`, 400 on violation); the admin panel
 * applies it as the toggles move via `applyOfferingDependencies` so a save
 * never carries a state the backend refuses. Runtime gates (`hasAnyFeature`,
 * `useHasFeature`, `canAccessRouteByFeature`) are single-key and NEVER consult
 * this map - "assistant implies ai" is a property of the stored list.
 */
export const OFFERING_DEPENDENCIES: Partial<Record<OfferingKey, OfferingKey>> = {
  assistant: "ai",
}

/**
 * Pure toggle step for the admin panel. Enabling a dependent key also enables
 * its requirement; disabling a required key also disables every key that
 * depends on it. Returns a new deduplicated list; never mutates `current`.
 */
export function applyOfferingDependencies(
  current: string[],
  key: string,
  enabled: boolean,
): string[] {
  const next = new Set(current)
  if (enabled) {
    next.add(key)
    const required = OFFERING_DEPENDENCIES[key as OfferingKey]
    if (required) next.add(required)
  } else {
    next.delete(key)
    for (const [dependent, required] of Object.entries(OFFERING_DEPENDENCIES)) {
      if (required === key) next.delete(dependent)
    }
  }
  return [...next]
}

/**
 * The dashboard is reachable only when the firm holds at least one of these
 * (any-of). A firm with only requests, customers or integrations has no lab,
 * QC or plant activity for it to summarise. Consumed by the route gate
 * (`FEATURE_ROUTES`) and the sidebar so the two cannot disagree.
 */
export const DASHBOARD_FEATURES: OfferingKey[] = ["eln", "qc", "production"]

/**
 * Always-on / all-tenant modules - never feature-gated. Rendered read-only in
 * the admin panel's "Always included" footer; NEVER as toggles.
 */
export const ALWAYS_INCLUDED: { label: string; modules: { name: string; route: string }[] }[] = [
  {
    label: "Always-on",
    modules: [
      { name: "Projects", route: "/rnd" },
      { name: "SOPs", route: "/sop-library" },
      { name: "Settings / Administration", route: "/settings" },
    ],
  },
  {
    // The dashboard summarises lab, QC and plant work, so a firm with none of
    // those has nothing for it to show (owner, 2026-10-09). Not toggleable on
    // its own - it follows DASHBOARD_FEATURES.
    label: "With ELN, QC or Production",
    modules: [{ name: "Dashboard", route: "/dashboard" }],
  },
  {
    label: "All-tenant",
    modules: [
      { name: "Raw Materials", route: "/raw-materials" },
      { name: "Suppliers", route: "/suppliers" },
      { name: "Customers", route: "/customers" },
      { name: "Products", route: "/products" },
    ],
  },
]

/**
 * Any-of entitlement check. Returns true iff `enabledFeatures` contains at
 * least one of `keys`:
 *   - a `string` key  → enabledFeatures.includes(key)
 *   - a `string[]`    → keys.some(k => enabledFeatures.includes(k))
 * An empty `keys` array returns false. Consumed by both the sidebar filter and
 * FeatureGuard so the gating logic is never duplicated.
 */
export function hasAnyFeature(
  enabledFeatures: string[],
  keys: string | string[],
): boolean {
  const list = Array.isArray(keys) ? keys : [keys]
  return list.some((k) => enabledFeatures.includes(k))
}

/**
 * True when a firm holds the ELN offering and neither QC nor Production — the
 * one subscription shape that gets the R&D-throughput dashboard
 * (`TenantAdminDashboard`) instead of the Founder Cockpit.
 *
 * Deliberately a POSITIVE test. "Not qc and not production" is NOT the same
 * question: `Organization.enabled_features` is `default=list` (api/models.py),
 * so an unconfigured firm — the default for every firm created since migration
 * `api/0027` — reads `[]`, and a localStorage session cached before the field
 * existed deserialises it as `undefined`. Neither is an ELN-only firm; both are
 * unknown, and unknown must keep the surface it has today. Accepting
 * `string[] | undefined` puts that fallback inside the predicate so no caller
 * can forget it.
 *
 * The `ai` and `assistant` offerings are independent axes and intentionally
 * ignored here: an eln+ai firm is ELN-only for routing, and its AI card is
 * gated separately on `hasAnyFeature(features, "ai")`.
 *
 * → `.workflow/goals/eln-only-tenant-dashboard/prd.md` §3.5
 */
export function isElnOnlyFirm(enabledFeatures: string[] | undefined): boolean {
  const features = enabledFeatures ?? []
  return (
    hasAnyFeature(features, "eln") &&
    !hasAnyFeature(features, ["qc", "production"])
  )
}
